import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Trash2, ArrowLeft, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import type { Document } from "@/types/documents";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { formatFileSize } from "@/utils/formatFileSize";
import { ConfirmDialog } from "./molecules/ConfirmDialog";

interface TrashViewProps {
  trashedDocuments: Document[];
  onRestore: (document: Document) => Promise<void>;
  onPermanentDelete: (document: Document) => Promise<void>;
  /** Permanently delete the user's current selection (single batch). */
  onBulkPermanentDelete: (documents: Document[]) => Promise<void>;
  /** Restore the user's current selection (single batch). */
  onBulkRestore: (documents: Document[]) => Promise<void>;
  onEmptyTrash: () => Promise<void>;
  /** Whether the current user may hard-delete (permanent delete / empty trash).
   *  Building view → org DELETE_DATA; project view → project DELETE_PROJECT_FILES.
   *  When false the view is restore-only: no checkboxes, no permanent-delete UI. */
  canPermanentlyDelete: boolean;
  /**
   * Where permanent delete is a per-project permission the member could be
   * given (DELETE_PROJECT_FILES), the delete controls stay offered and a press
   * calls this to refuse it with a message, instead of vanishing.
   */
  onPermanentDeleteDenied?: () => void;
  isLoading: boolean;
  onBack?: () => void;
}

function formatDeletedDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd-MM-yyyy");
}

export function TrashView({
  trashedDocuments,
  onRestore,
  onPermanentDelete,
  onBulkPermanentDelete,
  onBulkRestore,
  onEmptyTrash,
  canPermanentlyDelete,
  onPermanentDeleteDenied,
  isLoading,
  onBack,
}: TrashViewProps) {
  const offerPermanentDelete = canPermanentlyDelete || !!onPermanentDeleteDenied;
  const { t } = useLanguage();
  const trash = t.documents.trash;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBusy, setIsBusy] = useState(false);
  const [docToDelete, setDocToDelete] = useState<Document | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);

  // Drop any selected ids that are no longer in the trash (after a restore /
  // permanent delete elsewhere, or a refetch) so the selection never points at
  // documents that have already left the list.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(trashedDocuments.map((d) => d.id));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (present.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [trashedDocuments]);

  const selectedDocuments = useMemo(
    () => trashedDocuments.filter((d) => selectedIds.has(d.id)),
    [trashedDocuments, selectedIds]
  );
  const selectedCount = selectedDocuments.length;
  const allSelected = trashedDocuments.length > 0 && selectedCount === trashedDocuments.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const showSelection = canPermanentlyDelete;

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(trashedDocuments.map((d) => d.id)) : new Set());
  };
  const clearSelection = () => setSelectedIds(new Set());

  // All async actions run through this so the UI disables every button while
  // one is in flight (prevents double-submit) and surfaced errors are logged
  // rather than left as unhandled promise rejections (the hooks already toast).
  const withBusy = async (action: () => Promise<unknown>) => {
    setIsBusy(true);
    try {
      await action();
    } catch (error) {
      console.error("Trash action failed:", error);
    } finally {
      setIsBusy(false);
    }
  };

  const handleRestore = (doc: Document) => withBusy(() => onRestore(doc));

  const handleConfirmSingleDelete = () => {
    const doc = docToDelete;
    if (!doc) return;
    setDocToDelete(null);
    void withBusy(() => onPermanentDelete(doc));
  };

  const handleConfirmBulkDelete = () => {
    const docs = selectedDocuments;
    setShowBulkDeleteConfirm(false);
    void withBusy(async () => {
      await onBulkPermanentDelete(docs);
      clearSelection();
    });
  };

  const handleBulkRestore = () => {
    const docs = selectedDocuments;
    void withBusy(async () => {
      await onBulkRestore(docs);
      clearSelection();
    });
  };

  const handleConfirmEmptyTrash = () => {
    setShowEmptyTrashConfirm(false);
    void withBusy(async () => {
      await onEmptyTrash();
      clearSelection();
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">{trash.loadingTrash}</p>
        </div>
      </div>
    );
  }

  const hasDocuments = trashedDocuments.length > 0;
  const colSpan = showSelection ? 5 : 4;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{trash.title}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{trash.description}</p>
            </div>

            <div className="flex items-center gap-2">
              {onBack && (
                <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  {trash.backButton}
                </Button>
              )}

              {offerPermanentDelete && hasDocuments && (
                <Button
                  variant="destructive"
                  onClick={() =>
                    canPermanentlyDelete
                      ? setShowEmptyTrashConfirm(true)
                      : onPermanentDeleteDenied?.()
                  }
                  disabled={isBusy}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {trash.emptyTrashButton}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {!hasDocuments ? (
            <div className="py-12 text-center">
              <Trash2 className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">{trash.emptyTitle}</h3>
              <p className="text-gray-500">{trash.emptyDescription}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant="secondary">
                  {trash.documentCount.replace("{count}", String(trashedDocuments.length))}
                </Badge>
                <p className="text-sm text-muted-foreground">{trash.purgeWarning}</p>
              </div>

              {showSelection && selectedCount > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2">
                  <span className="text-sm font-medium">
                    {trash.selectedCount.replace("{count}", String(selectedCount))}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBulkRestore}
                      disabled={isBusy}
                    >
                      <RotateCcw className="mr-1.5 h-4 w-4" />
                      {trash.restore}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      disabled={isBusy}
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" />
                      {trash.permanentDelete}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={clearSelection} disabled={isBusy}>
                      {trash.clearSelection}
                    </Button>
                  </div>
                </div>
              )}

              <TooltipProvider>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30 text-left text-muted-foreground">
                        {showSelection && (
                          <th className="w-10 px-3 py-2">
                            <Checkbox
                              checked={allSelected ? true : someSelected ? "indeterminate" : false}
                              onCheckedChange={(checked) => toggleAll(checked === true)}
                              aria-label={trash.selectAll}
                            />
                          </th>
                        )}
                        <th className="px-3 py-2 font-medium">{trash.nameColumn}</th>
                        <th className="px-3 py-2 font-medium">{trash.deletedColumn}</th>
                        <th className="px-3 py-2 font-medium">{trash.sizeColumn}</th>
                        <th className="px-3 py-2" aria-hidden="true" />
                      </tr>
                    </thead>
                    <tbody>
                      {trashedDocuments.length === 0 ? (
                        <tr>
                          <td
                            colSpan={colSpan}
                            className="px-3 py-6 text-center text-muted-foreground"
                          >
                            {trash.emptyTitle}
                          </td>
                        </tr>
                      ) : (
                        trashedDocuments.map((doc) => {
                          const isSelected = selectedIds.has(doc.id);
                          return (
                            <tr
                              key={doc.id}
                              className="border-b last:border-b-0 hover:bg-muted/20"
                              data-state={isSelected ? "selected" : undefined}
                            >
                              {showSelection && (
                                <td className="px-3 py-2 align-middle">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={(checked) =>
                                      toggleOne(doc.id, checked === true)
                                    }
                                    aria-label={doc.filename}
                                  />
                                </td>
                              )}
                              <td className="px-3 py-2 align-middle">
                                <div className="flex min-w-0 items-center gap-2">
                                  <FileTypeIcon
                                    filename={doc.filename}
                                    contentType={doc.content_type}
                                    className="shrink-0"
                                  />
                                  <span className="truncate" title={doc.filename}>
                                    {doc.filename}
                                  </span>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 align-middle text-muted-foreground">
                                {formatDeletedDate(doc.deleted_at ?? doc.created_at)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 align-middle text-muted-foreground">
                                {formatFileSize(doc.size ?? 0)}
                              </td>
                              <td className="px-3 py-2 align-middle">
                                <div className="flex items-center justify-end gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => handleRestore(doc)}
                                        disabled={isBusy}
                                        aria-label={trash.restore}
                                      >
                                        <RotateCcw className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>{trash.restore}</TooltipContent>
                                  </Tooltip>

                                  {offerPermanentDelete && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                          onClick={() =>
                                            canPermanentlyDelete
                                              ? setDocToDelete(doc)
                                              : onPermanentDeleteDenied?.()
                                          }
                                          disabled={isBusy}
                                          aria-label={trash.permanentDelete}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>{trash.permanentDelete}</TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </TooltipProvider>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Single permanent-delete confirmation */}
      <ConfirmDialog
        open={!!docToDelete}
        onOpenChange={(open) => {
          if (!open) setDocToDelete(null);
        }}
        title={trash.permanentDeleteTitle}
        description={
          docToDelete
            ? trash.permanentDeleteConfirm.replace("{filename}", docToDelete.filename)
            : trash.permanentDeleteConfirmFallback
        }
        onConfirm={handleConfirmSingleDelete}
        confirmLabel={trash.confirmPermanentDelete}
        cancelLabel={t.common.cancel}
        variant="destructive"
      />

      {/* Bulk permanent-delete confirmation */}
      <ConfirmDialog
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        title={trash.bulkPermanentDeleteTitle}
        description={trash.bulkPermanentDeleteConfirm.replace("{count}", String(selectedCount))}
        onConfirm={handleConfirmBulkDelete}
        confirmLabel={trash.confirmPermanentDelete}
        cancelLabel={t.common.cancel}
        variant="destructive"
      />

      {/* Empty-trash confirmation */}
      <ConfirmDialog
        open={showEmptyTrashConfirm}
        onOpenChange={setShowEmptyTrashConfirm}
        title={trash.emptyTrashTitle}
        description={trash.emptyTrashConfirm.replace("{count}", String(trashedDocuments.length))}
        onConfirm={handleConfirmEmptyTrash}
        confirmLabel={trash.emptyTrashButton}
        cancelLabel={t.common.cancel}
        variant="destructive"
      />
    </div>
  );
}
